import { type HttpClient, query, segment } from '../http';

import {
  type AddScorePayload,
  type AddSessionPlayerPayload,
  type AdvanceTurnPayload,
  type CreateSessionPayload,
  type RecordSessionPayload,
  type GameStats,
  type ListSessionsQuery,
  type ListSessionsResult,
  type PlayerSummary,
  type SearchPlayersQuery,
  type Session,
  type UpdatableSessionStatus,
} from './sessions.types';

/**
 * Game sessions: the table, its scoreboard and its turn order.
 *
 * Warning: Every mutation answers the **whole session**, not a fragment. Replace
 * the state with what comes back rather than patching it locally: the server
 * also moves the turn, the winner and `elapsedSeconds` along the way.
 *
 * @param http Transport supplied by the application.
 * @returns The `sessions` domain of the API client.
 */
export function createSessionsApi(http: HttpClient) {
  return {
    /**
     * Lists sessions, paginated.
     *
     * Warning: `scope: 'family'` answers `session.error.list.not_in_family` (400)
     * when the caller has no household — the normal state of someone who never
     * joined one, not a failure.
     *
     * @param params Scope, status, game and pagination.
     * @returns One page of summaries with the total.
     */
    list: (params: ListSessionsQuery = {}) =>
      http.get<ListSessionsResult>(
        `/sessions${query({
          scope: params.scope,
          status: params.status,
          gameId: params.gameId,
          page: params.page,
          limit: params.limit,
        })}`,
      ),

    /**
     * Reads one session in full.
     *
     * @param id The session id.
     * @returns Scoreboard, turn order and status.
     * @throws 403 when the caller took no part in it, 404 when it does not exist.
     */
    findOne: (id: string) => http.get<Session>(`/sessions/${segment(id)}`),

    /**
     * Searches who can be seated: the caller's guests, their own account, and
     * any household member or accepted friend.
     *
     * A player profile is provisioned on the fly for anyone in that circle who
     * has never played, so a brand-new friend still shows up.
     *
     * @param params Free text and page size.
     * @returns The usable player profiles.
     */
    searchPlayers: (params: SearchPlayersQuery = {}) =>
      http.get<readonly PlayerSummary[]>(
        `/sessions/players/search${query({ q: params.q, limit: params.limit })}`,
      ),

    /**
     * Opens a session; the caller is always seated first.
     *
     * Every other player is added directly — there is no invitation to accept,
     * only a live nudge for those connected.
     *
     * @param payload The game, and who is at the table.
     * @returns The created session.
     * @throws 404 `session.error.create.game_not_found`.
     */
    create: (payload: CreateSessionPayload) =>
      http.post<Session>('/sessions', payload),

    /**
     * Writes down a game played away from the app, in one call.
     *
     * Warning: Not `create` with a date. That one opens a game being played and
     * would leave everyone with an `activeSessionId` pointing at a game already
     * over. This writes a FINISHED session, dated when it happened, and touches
     * nobody's active session.
     *
     * The winner is decided server-side: the single highest score, or nobody
     * when the top score is shared.
     *
     * @param payload The game, when it was played, and one line per player.
     * @returns The recorded session.
     * @throws 404 `session.error.record.game_not_found`; 400 for a future date,
     *   an empty sheet, a duplicated player, or a line with both/neither name.
     */
    record: (payload: RecordSessionPayload) =>
      http.post<Session>('/sessions/record', payload),

    /**
     * Suspends or resumes a session (creator only).
     *
     * @param id The session id.
     * @param status The status to move to.
     * @returns The updated session.
     * @throws 400 when it has finished or already holds that status, 403 when
     *   the caller did not open it.
     */
    updateStatus: (id: string, status: UpdatableSessionStatus) =>
      http.patch<Session>(`/sessions/${segment(id)}/status`, { status }),

    /**
     * Hands the turn over (creator only).
     *
     * Warning: Send the `version` last read as `expectedVersion`. A 409
     * (`session.error.turn.version_conflict`) means someone moved first:
     * refetch and replay the gesture, never retry with the same number.
     *
     * @param id The session id.
     * @param payload The expected version, and optionally whom to jump to.
     * @returns The updated session.
     * @throws 400 when it is not active, 403 when not the creator, 409 on conflict.
     */
    advanceTurn: (id: string, payload: AdvanceTurnPayload) =>
      http.patch<Session>(`/sessions/${segment(id)}/turn`, payload),

    /**
     * Adjusts a score by a **relative** amount.
     *
     * Warning: `delta` adds to the running total; sending the total itself would
     * add it a second time. Same optimistic locking as the turn, on this
     * player's own `score.version`.
     *
     * @param id The session id.
     * @param playerId The player whose score moves.
     * @param payload The adjustment and the expected version.
     * @returns The updated session.
     * @throws 403 `session.error.score.forbidden` when editing someone else's
     *   score without having opened the session, 409 on conflict.
     */
    addScore: (id: string, playerId: string, payload: AddScorePayload) =>
      http.patch<Session>(
        `/sessions/${segment(id)}/scores/${segment(playerId)}`,
        payload,
      ),

    /**
     * Seats someone mid-game (creator only): an existing profile, or a guest.
     *
     * @param id The session id.
     * @param payload Exactly one of `playerId` or `guestName`.
     * @returns The updated session.
     * @throws 400 when the session has finished or when neither/both fields
     *   were given, 409 when that player already sits at the table.
     */
    addPlayer: (id: string, payload: AddSessionPlayerPayload) =>
      http.post<Session>(`/sessions/${segment(id)}/players`, payload),

    /**
     * Takes a player out of the session.
     *
     * A soft removal: the participant keeps their score and their place in the
     * history, they simply stop playing.
     *
     * @param id The session id.
     * @param sessionPlayerId The **participant** id, not the player id.
     * @returns The updated session.
     * @throws 403 when the caller is neither the creator nor that player.
     */
    removePlayer: (id: string, sessionPlayerId: string) =>
      http.delete<Session>(
        `/sessions/${segment(id)}/players/${segment(sessionPlayerId)}`,
      ),

    /**
     * Ends the game (creator only): settles the winner(s), freezes the elapsed
     * time and frees every participant's `activeSessionId`.
     *
     * Ties are kept — several players can come back marked `isWinner`.
     *
     * @param id The session id.
     * @returns The final session.
     * @throws 400 when it had already finished, 403 when not the creator.
     */
    finish: (id: string) =>
      http.post<Session>(`/sessions/${segment(id)}/finish`),

    /**
     * The players behind a group's other members, ready to be seated.
     *
     * Warning: the reader is **not** among them — they are already at their
     * own table. Missing player rows are created on the way.
     *
     * @param conversationId The group.
     * @returns One summary per other member.
     * @throws 403 when the reader is not in that group.
     */
    seatGroup: (conversationId: string) =>
      http.get<PlayerSummary[]>(
        `/sessions/players/groups/${segment(conversationId)}`,
      ),

    /**
     * Reads a game's three leaderboards, top three each.
     *
     * @param gameId The game to rank on.
     * @returns Best winrate, best score and most playing time.
     */
    getGameStats: (gameId: string) =>
      http.get<GameStats>(`/sessions/stats/games/${segment(gameId)}`),
  };
}

export type SessionsApi = ReturnType<typeof createSessionsApi>;
